<template>
  <div
    v-if="event"
    ref="eventEl"
    v-hotkey="shortcuts"
    class="inbox-event"
    :class="{ isSelected }"
    @click.stop="onClick"
    @dblclick.stop.prevent="openEvent"
    @contextmenu.prevent.stop="showContextMenu"
  >
    <div class="datetime">
      <div class="day">
        {{ dayDisplay }}
      </div>
      <span class="time">-</span>
      <div v-if="!event.allDay" class="time">
        {{ timeDisplay }}
      </div>
      <div v-else class="time">All day</div>
    </div>
    <div class="description">
      <BaseEventName
        :event-title="event.title"
        :event-color="eventColorStyle"
        class="event-name"
      />
      <BaseAttendeesText
        v-if="organizer && event.attendees"
        :organizer="organizer"
        :attendees="event.attendees"
        class="event-attendees"
      />
    </div>
    <EventAttendanceButtons :event="event" />
  </div>
</template>
<script lang="ts">
import { computedAsync } from "@vueuse/core";
import { format } from "date-fns";
import { computed, defineComponent, ref, toRefs, watch } from "vue";

import { FullEvent } from "@ctrl/controller-rpc";

import { eventActions } from "@/components/actions/actions";
import BaseAttendeesText from "@/components/base/BaseAttendeesText.vue";
import BaseEventName from "@/components/base/BaseEventName.vue";
import { formatStartEndTime } from "@/components/dates";
import EventAttendanceButtons from "@/components/EventAttendanceButtons.vue";
import * as inject from "@/components/inject";
import { getEventShortcuts } from "@/components/object-shortcuts";
import {
  assignShortcuts,
  Shortcuts,
  unSelectItem,
} from "@/components/shortcuts";
import { eventColor, isCancellable } from "@/event";
import { bindAsync, useReleaser } from "@/ref";

import { useEventInfo } from "../eventInfo";

export default defineComponent({
  components: { BaseAttendeesText, BaseEventName, EventAttendanceButtons },
  props: {
    isSelected: Boolean,
    eventId: {
      type: String,
      required: true,
    },
  },
  emits: {
    selectEvent: (_event: FullEvent) => true,
    unselect: () => true,
    setSelectedEventEl: (_el: HTMLElement) => true,
  },
  async setup(props, { emit }) {
    const { eventId, isSelected } = toRefs(props);
    const contextMenu = inject.contextMenu();
    const controller = inject.controller();
    const logger = inject.logger();
    const profile = inject.profile();
    const calendars = inject.calendars();
    const locale = inject.locale();
    const multipleEventsModal = inject.multipleEventsModal();
    const router = inject.router();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const menu = inject.menu();
    const palette = inject.palette();
    const simpleModal = inject.simpleModal();
    const theme = inject.theme();
    const eventEl = ref<HTMLElement>();

    // computedAsync must be before any await to ensure proper cleanup on unmount
    // We declare it here but it will reactively update once `event` is populated
    const eventRef = ref<FullEvent | null>(null);
    const organizer = computedAsync(async () => {
      if (eventRef.value?.creator) {
        const person = await controller.people.byEmail(
          eventRef.value.creator.email
        );
        person.release();
        return person.value?.name?.display || eventRef.value.creator.email;
      } else return null;
    });

    const releaser = useReleaser();
    const eventPromise = releaser.add(
      bindAsync(eventId, (id) => Promise.resolve(controller.event.get(id)))
    );
    const eventInfo = eventPromise.then((event) =>
      useEventInfo(controller, calendars, event)
    );
    releaser.add(
      eventInfo.then((info) => ({ release: () => info.releaseEventInfo() }))
    );
    const [{ calendar, isWritable }, event] = await Promise.all([
      eventInfo,
      eventPromise,
    ]);

    // Sync the event ref for the computedAsync
    releaser.defer(
      watch(event, (e) => (eventRef.value = e ?? null), { immediate: true })
    );
    const eventColorStyle = computed(() => eventColor(calendar, theme));
    const dayDisplay = computed(() =>
      event.value?.start ? format(event.value.start, "EEEE, MMMM do") : ""
    );
    const timeDisplay = computed(() =>
      event.value?.start && event.value?.end
        ? formatStartEndTime(event.value.start, event.value.end, locale.value)
        : null
    );
    const openEvent = () =>
      event.value
        ? eventActions.open.action(
            {
              type: "event",
              value: event.value,
            },
            { controller, router }
          )
        : null;
    const onClick = () =>
      event.value ? emit("selectEvent", event.value) : null;
    const showContextMenu = async (e: MouseEvent) => {
      if (event.value) {
        onClick();
        contextMenu.value.show(
          {
            type: "event",
            value: {
              ...event.value,
              isCancellable: event.value
                ? await isCancellable(ref(event.value), controller)
                : false,
              isDeletable: !!calendar.value?.isWritable,
            },
          },
          e
        );
      }
    };
    const isEscapeActive = computed<boolean>(
      () => isSelected.value && !contextMenu.value.isDisplayed
    );
    const unselect = () => emit("unselect");
    releaser.defer(
      watch(isSelected, (value) => {
        if (value && eventEl.value) emit("setSelectedEventEl", eventEl.value);
      })
    );
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      if (isSelected.value && event.value)
        return {
          ...assignShortcuts(injection, unSelectItem, unselect, isEscapeActive),
          ...getEventShortcuts(
            {
              event,
              isWritable,
              calendar,
            },
            {
              ...injection,
              controller,
              logger,
              profile,
              router,
              simpleModal,
              multipleEventsModal,
              menu,
              palette,
            },
            { closeContextMenu: contextMenu.value.hide }
          ),
        };
      else return {};
    });
    return {
      dayDisplay,
      event,
      eventEl,
      eventColorStyle,
      onClick,
      openEvent,
      organizer,
      shortcuts,
      showContextMenu,
      timeDisplay,
    };
  },
});
</script>
<style lang="scss" scoped>
.inbox-event {
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  padding: 12px 16px;
  border: 1px solid transparent;
  border-radius: 3px;
  &:hover {
    @include themed() {
      border: 1px solid t($border-inline-element-hover);
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
    }
  }

  &.isSelected {
    @include themed() {
      border: 1px solid t($border-inline-element-selected);
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
    }
  }
  .datetime {
    display: flex;
    flex-direction: row;
    column-gap: 8px;
    @extend %font-small-regular;
    letter-spacing: 1px;
    text-transform: uppercase;
    .day {
      @extend %font-small-medium;
      @include themed() {
        color: t($primary);
      }
    }
    .time {
      @extend %font-small-regular;
      @include themed() {
        color: t($grey-60);
      }
    }
  }
  .description {
    display: flex;
    flex-direction: row;
    column-gap: 6px;
    align-items: center;
    .event-attendees {
      padding: 4px 0;
    }
  }
}
</style>
